import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Toolbar,
  ToolbarDivider,
  ToolbarGroup,
  useToastController,
} from '@fluentui/react-components';
import {
  Add20Regular,
  ArrowClockwise20Regular,
  ArrowDownload20Regular,
  ArrowMaximize20Regular,
  ArrowMinimize20Regular,
  ArrowSync20Regular,
  ArrowUndo20Regular,
  ArrowUpload20Regular,
  Calculator20Regular,
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  History20Regular,
  PersonAdd20Regular,
  Play20Regular,
  Settings20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuideSectionTitle } from '../../../theme/components/GuideSectionTitle';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideEmptyState } from '../../../theme/components/GuideEmptyState';
import { GuideSkeleton } from '../../../theme/components/GuideSkeleton';
import { GuideKpiCard, GuideKpiGrid } from '../../../theme/components/GuideKpiCard';
import { GuideBannerAction, GuideLinkButton } from '../../../theme/components/GuideButtons';
import ConsumptionDetailsCompoment from '../../Protocols/ConsumptionDetails/ConsumptionDetails.Component';
import { ImportConsumptionsDrawer } from '../ProviderConsumptionImport/ImportConsumptions.Drawer';
import { AssignProtocolPatientDrawer } from '../ProviderConsumptionImport/AssignProtocolPatient.Drawer';
import { ProviderConsumptionsFilters, ProviderConsumptionsFilterPatch } from './ProviderConsumptions.Filters';
import { ProviderConsumptionsTable } from './ProviderConsumptions.Table';
import { ProviderSyncCard } from './ProviderSync.Card';
import { ProviderSyncRequestDrawer } from './ProviderSyncRequest.Drawer';
import { ProviderSyncRunsDrawer } from './ProviderSyncRuns.Drawer';
import { ProviderSyncAlarmsDrawer } from './ProviderSyncAlarms.Drawer';
import { useSyncPolling } from './useSyncPolling';
import { ImportJobsHistoryDrawer } from './ImportJobsHistory.Drawer';
import { ImportSettingsDrawer } from './ImportSettings.Drawer';
import { UndoImportDialog } from './UndoImport.Dialog';
import { ToSettleRejectedList } from './ToSettleRejected.List';
import { ProviderToSettleBar } from './ProviderToSettle.Bar';
import { ProviderConsumptionDetailsDrawer } from './ProviderConsumptionDetails.Drawer';
import { GuideFullscreen } from '../../../theme/components/GuideFullscreen';
import { ImportJobProgressBar } from '../ProviderConsumptionImport/ImportJobProgress';
import { AppToast, APP_TOASTER_ID } from '../../../commons/component/AppToast/AppToast.Component';
import { isImportJobActive, isJobOfProvider } from '../../../functions/ImportJobs.Functions';
import AzureFileUploadDrawer from '../../Commons/AzureFileUploadDrawer/AzureFileUploadDrawer.Component';
import { buildProviderFileTarget, PROVIDER_ALLOWED_FILE_TYPES } from '../../../functions/AzureFileUpload.Functions';

import {
  buildProviderConsumptionsQuery,
  buildProviderConsumptionsSheet,
  describeRejections,
  filteredToSettleBlockReason,
  RejectionRow,
  selectedMarkableIds,
  selectedUnmarkableIds,
  toSettleFilterOf,
  formatMoney,
  loadGridFullscreen,
  saveGridFullscreen,
  hasActiveFilters,
  initialProviderConsumptionsFilter,
  includesProviderFileImport,
  isDateRangeInvalid,
  isSyncActive,
  nextSort,
  normalizeOriginDependentFilters,
  protocolConsumptionLink,
  selectedAssignableIds,
  selectedGeneratableIds,
  shouldPoll,
  SYNC_POLL_INTERVAL_MS,
  syncJustFinished,
  undoImportCountOf,
  undoTargetOfJob,
} from '../../../functions/ProviderConsumptions.Functions';
import { fileDisplayName, getApiErrorMessage, importableFiles, toggleAll, toggleId } from '../../../functions/ProviderConsumptionImport.Functions';
import { buildFileName, downloadWorkbook } from '../../Contracts/statistics/StatisticsExcel.Functions';
import {
  ProviderConsumption,
  ProviderConsumptionOrigin,
  ProviderConsumptionsFilter,
  ProviderConsumptionTotals,
  ProviderSyncRun,
  ProviderToSettleRequest,
  ProviderToSettleResult,
  PROVIDER_TO_SETTLE_FILTER_MAX,
  RevalueImportedResult,
  UndoImportTarget,
} from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { GenerateProtocolConsumptionsResult, ProviderImportJob } from '../../../models/Entities/ProviderConsumptionImport/ProviderConsumptionImport';
import { ProviderFileViewModel } from '../../../models/Entities/AzureFiles/AzureFileUpload';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ErrorType } from '../../../models/HttpError';
import { mapDispatchToProps, mapStateToProps, ProviderConsumptionsProps } from './ProviderConsumptions.Connect';
import '../ProviderConsumptionImport/ProviderConsumptionImport.Styles.css';
import './ProviderConsumptions.Styles.css';

interface ConsumptionDetailTarget {
  protocolId: number;
  consumptionId: string;
}

/** Resultado del marcado "Por liquidar" con las filas de rechazo armadas con la página de ese momento. */
interface ToSettleOutcome extends ProviderToSettleResult {
  toSettle: boolean;
  rows: RejectionRow[];
}

/**
 * Solapa "Consumos" del proveedor (spec 006): grilla unificada de los consumos del proveedor de todos
 * los orígenes (sincronizados con su valorización, manuales e importados de archivo), con totales,
 * sincronización (con seguimiento), historial y alarmas, acciones de importación de la spec 005 y Excel.
 */
export const ProviderConsumptionsSection = (props: ProviderConsumptionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.${path}`, options);
  const tri = (path: string, options?: Record<string, unknown>) => t(`providerConsumptionImport.${path}`, options);
  const { providerId, providerName, onOriginsChange } = props;

  // ---- filtros y grilla ----
  const [filter, setFilter] = useState<ProviderConsumptionsFilter>(() => {
    const initial = initialProviderConsumptionsFilter(props.initialOrigins ?? []);
    // deep-link "Ver consumos" del aviso (spec 008): ?origins=5&providerFileId=
    return props.initialProviderFileId && includesProviderFileImport(initial.origins) ? { ...initial, providerFileId: props.initialProviderFileId } : initial;
  });
  const [items, setItems] = useState<ProviderConsumption[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<ErrorType | undefined>(undefined);
  const [selected, setSelected] = useState<string[]>([]);
  const listRequest = useRef(0);

  // ---- totales ----
  const [totals, setTotals] = useState<ProviderConsumptionTotals | undefined>(undefined);
  const [totalsError, setTotalsError] = useState<ErrorType | undefined>(undefined);
  const totalsRequest = useRef(0);

  // ---- sincronización ----
  const [lastRun, setLastRun] = useState<ProviderSyncRun | null | undefined>(undefined);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState<ErrorType | undefined>(undefined);
  const [waiting, setWaiting] = useState(false);
  const [conflict, setConflict] = useState(false);

  // ---- listas de apoyo ----
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [protocolsLoading, setProtocolsLoading] = useState(false);
  const [protocolsError, setProtocolsError] = useState<unknown>(undefined);
  const [files, setFiles] = useState<ProviderFileViewModel[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<ErrorType | undefined>(undefined);

  // ---- drawers y acciones ----
  const [importOpen, setImportOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [syncOpen, setSyncOpen] = useState(false);
  const [runsOpen, setRunsOpen] = useState(false);
  const [alarmsRun, setAlarmsRun] = useState<ProviderSyncRun | undefined>(undefined);
  const [confirmGenerate, setConfirmGenerate] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateResult, setGenerateResult] = useState<GenerateProtocolConsumptionsResult | undefined>(undefined);
  const [generateError, setGenerateError] = useState<unknown>(undefined);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<unknown>(undefined);
  const [detail, setDetail] = useState<ConsumptionDetailTarget | undefined>(undefined);
  /** Drawer de detalle del consumo de proveedor (spec 010, US8). */
  const [detailItem, setDetailItem] = useState<ProviderConsumption | undefined>(undefined);
  /** Se incrementa después de una acción hecha fuera del drawer (asignación): el detalle vuelve a pedir el consumo. */
  const [detailRefresh, setDetailRefresh] = useState(0);
  /** Asignación de un solo consumo desde el detalle (el drawer de detalle se oculta mientras tanto). */
  const [assignTargets, setAssignTargets] = useState<ProviderConsumption[] | undefined>(undefined);

  // ---- importación en segundo plano (spec 008) y revalorización ----
  const [importFileId, setImportFileId] = useState<string | undefined>(undefined);
  const [jobsOpen, setJobsOpen] = useState(false);
  /** "Configuración de importación" (spec 009, DEC-APP-001): correspondencias de servicios (y formatos con US5). */
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [confirmRevalue, setConfirmRevalue] = useState(false);
  const [revalueScopeFile, setRevalueScopeFile] = useState(true);
  const [revaluing, setRevaluing] = useState(false);
  const [revalueError, setRevalueError] = useState<unknown>(undefined);
  /** "Deshacer importación" de un archivo (spec 010, US7): un solo diálogo para el historial, el asistente y la grilla. */
  const [undoTarget, setUndoTarget] = useState<UndoImportTarget | undefined>(undefined);

  // ---- marcado "Por liquidar" (spec 010, US1) ----
  const [toSettleBusy, setToSettleBusy] = useState<'mark' | 'unmark' | undefined>(undefined);
  /** Diálogo de "Todos los filtrados": marcar (true) o quitar (false). */
  const [toSettleConfirm, setToSettleConfirm] = useState<boolean | undefined>(undefined);
  const [toSettleResult, setToSettleResult] = useState<ToSettleOutcome | undefined>(undefined);
  const [toSettleError, setToSettleError] = useState<{ error: unknown; request: ProviderToSettleRequest } | undefined>(undefined);
  /** Se incrementa con cada recarga: la barra "Por liquidar" (US2) vuelve a pedir sus totales. */
  const [toSettleRefresh, setToSettleRefresh] = useState(0);
  const { dispatchToast } = useToastController(APP_TOASTER_ID);

  // ---- vista ampliada de la grilla (spec 008), recordada en la sesión ----
  const [fullscreen, setFullscreen] = useState<boolean>(() => loadGridFullscreen());
  const toggleFullscreen = (value: boolean) => {
    setFullscreen(value);
    saveGridFullscreen(value);
  };

  const datesInvalid = isDateRangeInvalid(filter.dateFrom, filter.dateTo);
  const totalsKey = buildProviderConsumptionsQuery(filter, { paged: false });

  const loadList = useCallback(async () => {
    if (!providerId || datesInvalid) return;
    const requestId = ++listRequest.current;
    setLoading(true);
    setLoadError(undefined);
    try {
      const page = await props.LoadProviderConsumptions(providerId, filter);
      if (requestId !== listRequest.current) return;
      setItems(page.items);
      setTotal(page.totalRecords);
      setSelected((current) => current.filter((id) => page.items.some((item) => item.id === id)));
    } catch (error: unknown) {
      if (requestId === listRequest.current) setLoadError(error as ErrorType);
    } finally {
      if (requestId === listRequest.current) {
        setLoading(false);
        setLoaded(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, filter, datesInvalid]);

  const loadTotals = useCallback(async () => {
    if (!providerId || datesInvalid) return;
    const requestId = ++totalsRequest.current;
    setTotalsError(undefined);
    try {
      const next = await props.LoadProviderConsumptionTotals(providerId, filter);
      if (requestId === totalsRequest.current) setTotals(next);
    } catch (error: unknown) {
      if (requestId === totalsRequest.current) setTotalsError(error as ErrorType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, totalsKey, datesInvalid]);

  const loadLastSync = useCallback(async () => {
    if (!providerId) return;
    setSyncLoading(true);
    setSyncError(undefined);
    try {
      setLastRun(await props.LoadLastProviderSync(providerId));
    } catch (error: unknown) {
      setSyncError(error as ErrorType);
    } finally {
      setSyncLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  const loadFiles = useCallback(async () => {
    if (!providerId) return;
    setFilesLoading(true);
    setFilesError(undefined);
    try {
      setFiles(await props.LoadImportableProviderFiles(providerId));
    } catch (error: unknown) {
      setFilesError(error as ErrorType);
    } finally {
      setFilesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadTotals();
  }, [loadTotals]);

  useEffect(() => {
    loadLastSync();
    loadFiles();
  }, [loadLastSync, loadFiles]);

  useEffect(() => {
    let alive = true;
    setProtocolsLoading(true);
    props
      .LoadProtocolsForAssignment()
      .then((data) => alive && setProtocols(data ?? []))
      .catch((error: unknown) => alive && setProtocolsError(error))
      .finally(() => alive && setProtocolsLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Orígenes elegidos → URL (?origins=), para que el vínculo conserve el filtro.
  const originsKey = (filter.origins ?? []).join(',');
  const firstOrigins = useRef(true);
  useEffect(() => {
    if (firstOrigins.current) {
      firstOrigins.current = false;
      return;
    }
    onOriginsChange?.(filter.origins ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originsKey]);

  const reloadAll = () => {
    loadList();
    loadTotals();
    setToSettleRefresh((current) => current + 1);
  };

  // ---- importación en segundo plano (spec 008) ----
  // "Reintentar" del aviso global: abre el asistente con el archivo (?importFileId=) y limpia la URL.
  useEffect(() => {
    if (!props.initialImportFileId) return;
    setImportFileId(props.initialImportFileId);
    setImportOpen(true);
    props.onImportFileConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.initialImportFileId]);

  // Jobs activos del proveedor (de cualquier usuario) al abrir: el aviso global los sigue sin avisar.
  useEffect(() => {
    if (!providerId || !props.LoadProviderImportJobs) return undefined;
    let alive = true;
    props
      .LoadProviderImportJobs(providerId)
      .then((jobs) => {
        if (!alive) return;
        (jobs ?? []).filter(isImportJobActive).forEach((job) => props.TrackImportJob?.(job, false));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  const jobsState = props.importJobsState;
  const activeJobs = useMemo(
    () =>
      Object.values(jobsState?.jobs ?? {})
        .filter((job) => isJobOfProvider(job, providerId) && isImportJobActive(job))
        .sort((a, b) => String(a.requestedAt ?? '').localeCompare(String(b.requestedAt ?? ''))),
    [jobsState?.jobs, providerId]
  );

  // Terminó un job de este proveedor: recarga grilla, totales y archivos.
  const reloadToken = jobsState?.reloadTokens?.[String(providerId)] ?? 0;
  const firstReloadToken = useRef(reloadToken);
  useEffect(() => {
    if (reloadToken === firstReloadToken.current) return;
    firstReloadToken.current = reloadToken;
    reloadAll();
    loadFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadToken]);

  const showConsumptionsOfFile = (fileId: string) =>
    setFilter((current) => ({
      ...current,
      origins: Array.from(new Set([...(current.origins ?? []), ProviderConsumptionOrigin.ProviderFileImport])),
      providerFileId: fileId,
      assignmentStatus: undefined,
      pageNumber: 1,
    }));

  const openImport = (fileId?: string) => {
    setImportFileId(fileId);
    setImportOpen(true);
  };

  // ---- revalorizar importados (spec 008) ----
  const runRevalue = async () => {
    setConfirmRevalue(false);
    setRevaluing(true);
    setRevalueError(undefined);
    const fileId = revalueScopeFile ? filter.providerFileId : undefined;
    try {
      const summary: RevalueImportedResult = await props.RevalueImportedConsumptions(providerId, fileId ? { providerFileId: fileId } : {});
      dispatchToast(
        <AppToast
          intent={summary.unvalued > 0 ? 'warning' : 'success'}
          title={tri('revalue.resultTitle', { total: summary.total })}
          body={tri('revalue.resultText', { byFileValue: summary.byFileValue, byPriceList: summary.byPriceList, unvalued: summary.unvalued })}
          data-testid="pcs-revalue-toast"
        />,
        { intent: summary.unvalued > 0 ? 'warning' : 'success', timeout: 7000 }
      );
      reloadAll();
    } catch (error: unknown) {
      setRevalueError(error);
    } finally {
      setRevaluing(false);
    }
  };

  // ---- seguimiento de la sincronización (cada 5 s mientras está activa) ----
  const pollTick = async () => {
    const next = await props.LoadLastProviderSync(providerId);
    const finished = syncJustFinished(lastRun, next, waiting);
    setLastRun(next);
    setSyncError(undefined);
    if (waiting && (finished || isSyncActive(next))) setWaiting(false);
    if (finished) {
      setConflict(false);
      reloadAll();
    }
  };

  useSyncPolling({ enabled: !!providerId && shouldPoll(lastRun, waiting), intervalMs: SYNC_POLL_INTERVAL_MS, onTick: pollTick });

  const requestSync = async (protocolId?: number) => {
    const result = await props.RequestProviderSync(providerId, protocolId);
    setSyncOpen(false);
    if (result.conflict) {
      setConflict(true);
      setWaiting(true);
      return;
    }
    setConflict(false);
    if (result.run) {
      setLastRun(result.run);
      if (!isSyncActive(result.run)) reloadAll();
    } else {
      setWaiting(true);
    }
  };

  // ---- filtros ----
  const changeFilter = (patch: ProviderConsumptionsFilterPatch) =>
    setFilter((current) => normalizeOriginDependentFilters({ ...current, ...patch, pageNumber: 1 }));

  const clearFilters = () =>
    setFilter((current) => ({ ...initialProviderConsumptionsFilter([]), pageSize: current.pageSize, sortBy: current.sortBy, sortDirection: current.sortDirection }));

  // ---- importación (spec 005) ----
  const fileOptions = useMemo(() => importableFiles(files), [files]);
  const fileNameById = useMemo(() => {
    const map = new Map<string, string>();
    files.forEach((file) => map.set(String(file.id), fileDisplayName(file)));
    return map;
  }, [files]);

  /** Grilla filtrada por un archivo importado: se puede deshacer su importación desde la barra. */
  const filteredFileId = includesProviderFileImport(filter.origins) ? filter.providerFileId : undefined;
  const openUndoOfFilteredFile = () => {
    if (!filteredFileId) return;
    setUndoTarget({
      fileId: String(filteredFileId),
      fileName: fileNameById.get(String(filteredFileId)) ?? String(filteredFileId),
      count: undoImportCountOf(filter, total),
    });
  };

  const onUndoneImport = () => {
    reloadAll();
    loadFiles();
  };

  // ---- marcado "Por liquidar" (spec 010, US1) ----
  const toMark = selectedMarkableIds(items, selected);
  const toUnmark = selectedUnmarkableIds(items, selected);
  const filteredBlock = filteredToSettleBlockReason(total);

  const runToSettle = async (request: ProviderToSettleRequest) => {
    setToSettleConfirm(undefined);
    setToSettleBusy(request.toSettle ? 'mark' : 'unmark');
    setToSettleError(undefined);
    setToSettleResult(undefined);
    const snapshot = items;
    try {
      const result = await props.MarkProviderConsumptionsToSettle(request);
      setToSettleResult({ ...result, toSettle: request.toSettle, rows: describeRejections(result.rejected, snapshot) });
      setSelected([]);
      reloadAll();
    } catch (error: unknown) {
      // 400 / red: la selección queda como estaba y se puede reintentar
      setToSettleError({ error, request });
    } finally {
      setToSettleBusy(undefined);
    }
  };

  const filteredReason = (): string | undefined => {
    if (filteredBlock === 'empty') return tr('toSettle.actions.noneFiltered');
    if (filteredBlock === 'tooMany') return tr('toSettle.actions.tooMany', { max: PROVIDER_TO_SETTLE_FILTER_MAX });
    return undefined;
  };

  const renderToSettleMenu = (toSettle: boolean) => {
    const ids = toSettle ? toMark : toUnmark;
    const kind = toSettle ? 'mark' : 'unmark';
    const busy = toSettleBusy === kind;
    const selectedReason = ids.length === 0 ? tr(toSettle ? 'toSettle.actions.noneSelectedMark' : 'toSettle.actions.noneSelectedUnmark') : undefined;
    const filteredHint = filteredReason();
    return (
      <Menu positioning="below-start">
        <MenuTrigger disableButtonEnhancement>
          <MenuButton
            appearance={toSettle ? 'primary' : 'secondary'}
            icon={toSettle ? <CheckmarkCircle20Regular /> : <DismissCircle20Regular />}
            disabled={!!toSettleBusy}
            aria-busy={busy || undefined}
            data-testid={`pcs-${kind}-menu`}
          >
            {busy ? tr(toSettle ? 'toSettle.actions.marking' : 'toSettle.actions.unmarking') : tr(toSettle ? 'toSettle.actions.mark' : 'toSettle.actions.unmark')}
          </MenuButton>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem disabled={ids.length === 0} onClick={() => runToSettle({ ids, toSettle })} data-testid={`pcs-${kind}-selected`}>
              <span className="pcs-menu-option">
                <span>{tr('toSettle.actions.selected', { count: ids.length })}</span>
                {selectedReason && <span className="pcs-menu-option__hint">{selectedReason}</span>}
              </span>
            </MenuItem>
            <MenuItem disabled={!!filteredBlock} onClick={() => setToSettleConfirm(toSettle)} data-testid={`pcs-${kind}-filtered`}>
              <span className="pcs-menu-option">
                <span>{tr('toSettle.actions.filtered', { count: total })}</span>
                {filteredHint && <span className="pcs-menu-option__hint">{filteredHint}</span>}
              </span>
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  };

  const renderToSettleNotices = () => (
    <>
      {toSettleError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-to-settle-error">
          <MessageBarBody>
            <MessageBarTitle>{tr(toSettleError.request.toSettle ? 'toSettle.errors.mark' : 'toSettle.errors.unmark')}</MessageBarTitle>
            {getApiErrorMessage(toSettleError.error)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => runToSettle(toSettleError.request)} disabled={!!toSettleBusy} data-testid="pcs-to-settle-retry">
              {tr('toSettle.errors.retry')}
            </GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {toSettleResult && (
        <GuideMessageBar intent={toSettleResult.rejectedTotal > 0 ? 'warning' : 'success'} layout="multiline" data-testid="pcs-to-settle-result">
          <MessageBarBody>
            <MessageBarTitle>
              {tr(toSettleResult.toSettle ? 'toSettle.result.marked' : 'toSettle.result.unmarked', { count: toSettleResult.updated })}
            </MessageBarTitle>
            {toSettleResult.rejectedTotal > 0 && (
              <>
                <span className="pcs-strong">{tr('toSettle.result.rejectedTitle', { count: toSettleResult.rejectedTotal })}</span>
                <ToSettleRejectedList rows={toSettleResult.rows} total={toSettleResult.rejectedTotal} />
              </>
            )}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setToSettleResult(undefined)}>{tr('toSettle.result.dismiss')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
    </>
  );

  const toAssign = selectedAssignableIds(items, selected);
  const toGenerate = selectedGeneratableIds(items, selected);

  const onImported = (fileId: string) => {
    loadFiles();
    setFilter((current) => ({
      ...current,
      origins: Array.from(new Set([...(current.origins ?? []), ProviderConsumptionOrigin.ProviderFileImport])),
      providerFileId: fileId,
      assignmentStatus: undefined,
      pageNumber: 1,
    }));
    loadTotals();
  };

  const runGenerate = async () => {
    setConfirmGenerate(false);
    setGenerating(true);
    setGenerateError(undefined);
    setGenerateResult(undefined);
    try {
      setGenerateResult(await props.GenerateProtocolConsumptions(toGenerate));
      setSelected([]);
      reloadAll();
    } catch (error: unknown) {
      setGenerateError(error);
    } finally {
      setGenerating(false);
    }
  };

  // ---- Excel ----
  const exportExcel = async () => {
    setExporting(true);
    setExportError(undefined);
    try {
      const all = await props.LoadAllProviderConsumptions(providerId, filter);
      await downloadWorkbook(buildFileName(`consumos proveedor ${providerName}`), [buildProviderConsumptionsSheet(all, t)], {
        title: tr('excel.title', { name: providerName }),
        scope: hasActiveFilters(filter) ? tr('excel.scopeFiltered') : tr('excel.scopeAll'),
      });
    } catch (error: unknown) {
      setExportError(error);
    } finally {
      setExporting(false);
    }
  };

  // ---- detalle del consumo de protocolo ----
  const openConsumption = (item: ProviderConsumption) => {
    const link = protocolConsumptionLink(item);
    if (link) setDetail({ protocolId: link.protocolId, consumptionId: link.consumptionId });
  };

  // ---- liquidación del consumo liquidado: sólo desde el detalle del consumo (spec 010 US8, DEC-APP-020) ----
  const onOpenSettlement = (settlementId: number) => navigate(`/settlements/${settlementId}`);

  // ---- detalle del consumo de proveedor (spec 010 US8) ----
  const openProtocolConsumptionFromDetail = (item: ProviderConsumption) => {
    setDetailItem(undefined);
    openConsumption(item);
  };

  const closeAssign = () => {
    setAssignOpen(false);
    setAssignTargets(undefined);
  };

  const onAssignedFromDrawer = () => {
    reloadAll();
    if (assignTargets) setDetailRefresh((current) => current + 1);
  };

  const closeDetail = () => {
    setDetail(undefined);
    // Un cambio en el consumo de protocolo dispara una sincronización automática (trigger 3).
    loadLastSync();
  };

  // ---- render ----
  const filtered = hasActiveFilters(filter);

  const renderKpis = () => (
    <GuideKpiGrid data-testid="pcs-kpis">
      <GuideKpiCard
        label={tr('kpi.total')}
        value={totals ? totals.totalCount : '—'}
        detail={filtered ? tr('kpi.filtered') : tr('kpi.all')}
        valueTestId="pcs-kpi-total"
      />
      {(totals?.byCurrency ?? []).map((currency) => (
        <GuideKpiCard
          key={currency.currencyCode}
          label={tr('kpi.currency', { currency: currency.currencyCode })}
          value={formatMoney(currency.amount, currency.currencyCode)}
          tone="info"
          detail={tr('kpi.currencyDetail', { count: currency.count, alarms: currency.alarmCount })}
          data-testid={`pcs-kpi-currency-${currency.currencyCode}`}
        />
      ))}
      <GuideKpiCard
        label={tr('kpi.alarms')}
        value={totals ? totals.alarmCount : '—'}
        tone={totals && totals.alarmCount > 0 ? 'warning' : 'neutral'}
        detail={tr('kpi.alarmsDetail')}
        valueTestId="pcs-kpi-alarms"
      >
        {totals && (totals.alarmCount > 0 || filter.hasAlarm) && (
          <GuideLinkButton size="small" onClick={() => changeFilter({ hasAlarm: !filter.hasAlarm })} data-testid="pcs-kpi-toggle-alarm">
            {filter.hasAlarm ? tr('kpi.showAll') : tr('kpi.onlyAlarms')}
          </GuideLinkButton>
        )}
      </GuideKpiCard>
    </GuideKpiGrid>
  );

  /** Totales en una línea (vista ampliada: las tarjetas KPI quedan debajo del overlay). */
  const renderTotalsLine = () => (
    <div className="pcs-totals-line" data-testid="pcs-totals-line">
      <span>
        {tr('kpi.total')}: <strong>{totals ? totals.totalCount : '—'}</strong>
      </span>
      {(totals?.byCurrency ?? []).map((currency) => (
        <span key={currency.currencyCode}>
          {currency.currencyCode}: <strong>{formatMoney(currency.amount, currency.currencyCode)}</strong>
        </span>
      ))}
      <span>
        {tr('kpi.alarms')}: <strong>{totals ? totals.alarmCount : '—'}</strong>
      </span>
    </div>
  );

  const renderBody = () => {
    if (datesInvalid) return null;
    if (!loaded || (loading && items.length === 0)) return <GuideSkeleton rows={5} label={tr('loading')} data-testid="pcs-loading" />;
    if (loadError && items.length === 0) return null;
    if (items.length === 0) {
      return filtered ? (
        <GuideEmptyState title={tr('empty.filteredTitle')} text={tr('empty.filteredText')} data-testid="pcs-empty-filtered" />
      ) : (
        <GuideEmptyState
          title={tr('empty.title')}
          text={tr('empty.text')}
          data-testid="pcs-empty"
          action={
            <div className="pcs-actions">
              <Button appearance="primary" icon={<ArrowSync20Regular />} onClick={() => setSyncOpen(true)} disabled={shouldPoll(lastRun, waiting)}>
                {tr('sync.sync')}
              </Button>
              <Button appearance="secondary" icon={<ArrowUpload20Regular />} onClick={() => openImport()}>
                {tri('actions.import')}
              </Button>
            </div>
          }
        />
      );
    }
    return (
      <ProviderConsumptionsTable
        items={items}
        filter={filter}
        total={total}
        onSort={(sortBy) => setFilter((current) => nextSort(current, sortBy))}
        onPageChange={(pageNumber) => setFilter((current) => ({ ...current, pageNumber }))}
        onPageSizeChange={(pageSize) => setFilter((current) => ({ ...current, pageSize, pageNumber: 1 }))}
        selected={selected}
        onToggle={(id) => setSelected((current) => toggleId(current, id))}
        onToggleAll={(ids) => setSelected((current) => toggleAll(current, ids))}
        onOpenConsumption={openConsumption}
        onOpenDetail={setDetailItem}
        fileNameById={fileNameById}
        fill={fullscreen}
      />
    );
  };

  const uploadTarget = buildProviderFileTarget(providerId);
  const canMapServices = !!props.LoadServiceMappings && !!props.UpdateServiceMapping && !!props.DeleteServiceMapping;
  const canEditTemplates =
    !!props.LoadReferenceTemplates &&
    !!props.CreateReferenceTemplate &&
    !!props.UpdateReferenceTemplate &&
    !!props.DeleteReferenceTemplate &&
    !!props.ReorderReferenceTemplates &&
    !!props.TestReferenceTemplate;
  const canConfigureImport = canMapServices || canEditTemplates;
  const assignItems = items.filter((item) => toAssign.includes(item.id));
  const codeOf = (id: string) => items.find((item) => item.id === id)?.medicalServiceCode || id;

  return (
    <section className="pcs-section pcs-section--fill" aria-labelledby="pcs-title" data-testid="pcs-section">
      <GuideSectionTitle id="pcs-title" as="h2" title={tr('section.title')} description={tr('section.description')} />

      {renderKpis()}
      {totalsError && (
        <GuideMessageBar intent="warning" layout="multiline" data-testid="pcs-totals-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('errors.totals')}</MessageBarTitle>
            {getApiErrorMessage(totalsError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => loadTotals()}>{tr('errors.retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      <ProviderSyncCard
        run={lastRun}
        loading={syncLoading}
        error={syncError}
        conflict={conflict}
        waiting={waiting}
        onSync={() => setSyncOpen(true)}
        onRetry={loadLastSync}
        onOpenHistory={() => setRunsOpen(true)}
        onOpenAlarms={(run) => setAlarmsRun(run)}
      />

      <GuideFullscreen
        open={fullscreen}
        onClose={() => toggleFullscreen(false)}
        title={tr('fullscreen.title', { name: providerName })}
        closeLabel={tr('fullscreen.collapse')}
        headerExtra={renderTotalsLine()}
        data-testid="pcs-fullscreen"
      >
      {activeJobs.length > 0 && (
        <div className="pcs-import-jobs" data-testid="pcs-import-jobs-banner">
          {activeJobs.map((job: ProviderImportJob) => (
            <GuideMessageBar key={job.id} intent="info" layout="multiline" data-testid={`pcs-import-job-${job.id}`}>
              <MessageBarBody>
                <div className="pcs-import-job">
                  <MessageBarTitle>{tri('jobs.bannerTitle', { name: job.fileName || '—' })}</MessageBarTitle>
                  <ImportJobProgressBar job={job} size="small" detail={job.requestedByUserName ? tri('jobs.requestedBy', { name: job.requestedByUserName }) : undefined} />
                </div>
              </MessageBarBody>
              <MessageBarActions>
                <GuideBannerAction onClick={() => setJobsOpen(true)}>{tri('jobs.history.open')}</GuideBannerAction>
              </MessageBarActions>
            </GuideMessageBar>
          ))}
        </div>
      )}

      <ProviderConsumptionsFilters
        filter={filter}
        onChange={changeFilter}
        onClear={clearFilters}
        protocols={protocols}
        protocolsLoading={protocolsLoading}
        files={fileOptions}
      />

      <ProviderToSettleBar
        providerId={providerId}
        providerIdentifier={props.providerIdentifier}
        providerName={providerName}
        refreshKey={toSettleRefresh}
        LoadTotals={props.LoadProviderConsumptionTotals}
      />

      <Toolbar className="pcs-toolbar" aria-label={tr('toolbar.label')} data-testid="pcs-toolbar">
        <span className="pcs-toolbar__selection" aria-live="polite" data-testid="pcs-selection-count">
          {selected.length > 0 ? tr('toolbar.selected', { count: selected.length }) : items.length > 0 ? tr('toolbar.selectHint') : ''}
        </span>

        {/* Liquidación (spec 010, DEC-APP-022): marcar / quitar "Por liquidar", junto a la barra "Por liquidar" de arriba. */}
        <ToolbarGroup role="group" aria-labelledby="pcs-group-settle" className="pcs-toolbar__group" data-testid="pcs-group-settle">
          <span id="pcs-group-settle" className="pcs-toolbar__group-label">
            {tr('toolbar.groups.settle')}
          </span>
          {renderToSettleMenu(true)}
          {renderToSettleMenu(false)}
        </ToolbarGroup>

        <ToolbarDivider className="pcs-toolbar__divider" />

        {/* Importación: la principal como botón, el resto en "Más". */}
        <ToolbarGroup role="group" aria-labelledby="pcs-group-import" className="pcs-toolbar__group" data-testid="pcs-group-import">
          <span id="pcs-group-import" className="pcs-toolbar__group-label">
            {tr('toolbar.groups.import')}
          </span>
          <Button appearance="secondary" icon={<ArrowUpload20Regular />} onClick={() => openImport()} data-testid="pcs-open-import">
            {tri('actions.import')}
          </Button>
          <Menu positioning="below-start">
            <MenuTrigger disableButtonEnhancement>
              <MenuButton appearance="subtle" aria-label={tr('toolbar.moreLabel', { group: tr('toolbar.groups.import') })} data-testid="pcs-menu-import">
                {tr('toolbar.more')}
              </MenuButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<Add20Regular />} disabled={!uploadTarget} onClick={() => setUploadOpen(true)} data-testid="pcs-open-upload">
                  {t('azureFileUpload.buttons.uploadFile')}
                </MenuItem>
                <MenuItem icon={<History20Regular />} onClick={() => setJobsOpen(true)} data-testid="pcs-open-import-jobs">
                  {tri('jobs.history.open')}
                </MenuItem>
                {filteredFileId && (
                  <MenuItem icon={<ArrowUndo20Regular />} onClick={openUndoOfFilteredFile} data-testid="pcs-open-undo">
                    {tri('undo.actionFile', { name: fileNameById.get(String(filteredFileId)) ?? filteredFileId })}
                  </MenuItem>
                )}
                {canConfigureImport && (
                  <MenuItem icon={<Settings20Regular />} onClick={() => setSettingsOpen(true)} data-testid="pcs-open-import-settings">
                    {tri('importSettings.open')}
                  </MenuItem>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        </ToolbarGroup>

        <ToolbarDivider className="pcs-toolbar__divider" />

        {/* Asignación de los importados: asignar y generar (pasos seguidos) como botones; revalorizar en "Más". */}
        <ToolbarGroup role="group" aria-labelledby="pcs-group-assign" className="pcs-toolbar__group" data-testid="pcs-group-assign">
          <span id="pcs-group-assign" className="pcs-toolbar__group-label">
            {tr('toolbar.groups.assign')}
          </span>
          <Button
            appearance="secondary"
            icon={<PersonAdd20Regular />}
            disabled={toAssign.length === 0 || generating}
            onClick={() => setAssignOpen(true)}
            data-testid="pcs-open-assign"
          >
            {tri('actions.assign')}
          </Button>
          <Button
            appearance="secondary"
            icon={<Play20Regular />}
            disabled={toGenerate.length === 0 || generating}
            onClick={() => setConfirmGenerate(true)}
            data-testid="pcs-open-generate"
          >
            {generating ? tri('generate.generating') : tri('actions.generate')}
          </Button>
          <Menu positioning="below-start">
            <MenuTrigger disableButtonEnhancement>
              <MenuButton appearance="subtle" aria-label={tr('toolbar.moreLabel', { group: tr('toolbar.groups.assign') })} data-testid="pcs-menu-assign">
                {tr('toolbar.more')}
              </MenuButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<Calculator20Regular />}
                  disabled={revaluing}
                  onClick={() => {
                    setRevalueScopeFile(!!filter.providerFileId);
                    setConfirmRevalue(true);
                  }}
                  data-testid="pcs-open-revalue"
                >
                  {revaluing ? tri('revalue.running') : tri('revalue.action')}
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </ToolbarGroup>

        {/* Vista, a la derecha. */}
        <ToolbarGroup role="group" aria-labelledby="pcs-group-view" className="pcs-toolbar__group pcs-toolbar__group--view" data-testid="pcs-group-view">
          <span id="pcs-group-view" className="pcs-toolbar__group-label">
            {tr('toolbar.groups.view')}
          </span>
          <Button
            appearance="subtle"
            icon={<ArrowDownload20Regular />}
            disabled={exporting || total === 0}
            onClick={exportExcel}
            aria-busy={exporting || undefined}
            data-testid="pcs-export"
          >
            {exporting ? tr('toolbar.exporting') : tr('toolbar.export')}
          </Button>
          <Button appearance="subtle" icon={<ArrowClockwise20Regular />} disabled={loading} onClick={reloadAll} data-testid="pcs-refresh">
            {tri('actions.refresh')}
          </Button>
          <Button
            appearance="subtle"
            icon={fullscreen ? <ArrowMinimize20Regular /> : <ArrowMaximize20Regular />}
            onClick={() => toggleFullscreen(!fullscreen)}
            aria-pressed={fullscreen}
            data-testid="pcs-toggle-fullscreen"
          >
            {fullscreen ? tr('fullscreen.collapse') : tr('fullscreen.expand')}
          </Button>
        </ToolbarGroup>
      </Toolbar>

      {loadError && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-load-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('errors.load')}</MessageBarTitle>
            {getApiErrorMessage(loadError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => loadList()}>{tr('errors.retry')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {renderToSettleNotices()}

      {exportError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-export-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('errors.export')}</MessageBarTitle>
            {getApiErrorMessage(exportError)}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {revalueError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-revalue-error">
          <MessageBarBody>
            <MessageBarTitle>{tri('revalue.error')}</MessageBarTitle>
            {getApiErrorMessage(revalueError)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setRevalueError(undefined)}>{tri('generate.dismiss')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {generateError !== undefined && (
        <GuideMessageBar intent="error" layout="multiline" data-testid="pcs-generate-error">
          <MessageBarBody>
            <MessageBarTitle>{tri('errors.generate')}</MessageBarTitle>
            {getApiErrorMessage(generateError)}
          </MessageBarBody>
        </GuideMessageBar>
      )}

      {generateResult && (
        <GuideMessageBar
          intent={generateResult.withWarnings > 0 || generateResult.skipped.length > 0 ? 'warning' : 'success'}
          layout="multiline"
          data-testid="pcs-generate-result"
        >
          <MessageBarBody>
            <MessageBarTitle>{tri('generate.resultTitle')}</MessageBarTitle>
            {tri('generate.resultText', {
              generated: generateResult.generated,
              withWarnings: generateResult.withWarnings,
              skipped: generateResult.skipped.length,
            })}
            {generateResult.withWarnings > 0 && <span className="pcs-secondary">{tri('generate.warningsHint')}</span>}
            {generateResult.skipped.length > 0 && (
              <ul className="pci-list">
                {generateResult.skipped.map((skip) => (
                  <li key={skip.id}>{tri('generate.skippedItem', { code: codeOf(skip.id), reason: skip.reason })}</li>
                ))}
              </ul>
            )}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => setGenerateResult(undefined)}>{tri('generate.dismiss')}</GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}

      {renderBody()}
      </GuideFullscreen>

      {uploadTarget && (
        <AzureFileUploadDrawer
          open={uploadOpen}
          onDismiss={() => {
            setUploadOpen(false);
            loadFiles();
          }}
          title={t('azureFileUpload.titles.provider')}
          target={uploadTarget}
          allowedFileTypes={PROVIDER_ALLOWED_FILE_TYPES}
          fileListLoader="provider"
          allowMultiple
        />
      )}

      <ImportConsumptionsDrawer
        open={importOpen}
        onDismiss={() => setImportOpen(false)}
        providerId={providerId}
        providerName={providerName}
        files={files}
        filesLoading={filesLoading}
        filesError={filesError}
        onReloadFiles={loadFiles}
        initialFileId={importFileId}
        onPreview={(fileId, body) => props.PreviewFileImport(providerId, fileId, body)}
        onImport={(fileId, body) => props.ImportFileConsumptions(providerId, fileId, body)}
        onLoadPreviewItems={props.LoadPreviewItems ? (previewId, query) => props.LoadPreviewItems!(providerId, previewId, query) : undefined}
        onLoadPreviewItemIds={props.LoadPreviewItemIds ? (previewId, filter) => props.LoadPreviewItemIds!(providerId, previewId, filter) : undefined}
        onLoadProtocols={props.LoadProtocolsForAssignment}
        onLoadPatients={props.LoadProtocolPatients}
        onTestTemplate={props.TestReferenceTemplate ? (parts, texts) => props.TestReferenceTemplate!(providerId, { parts, texts }) : undefined}
        onCreateTemplate={props.CreateReferenceTemplate ? (body) => props.CreateReferenceTemplate!(providerId, body) : undefined}
        onLoadJob={props.LoadImportJob ? (jobId) => props.LoadImportJob(jobId) : undefined}
        onJobStarted={(job) => props.TrackImportJob?.(job, true)}
        onJobForeground={(jobId, foreground) => props.SetImportJobForeground?.(jobId, foreground)}
        onJobFinished={(job) => props.AcknowledgeImportJobNotice?.(job.id)}
        onImported={onImported}
        onDeleteImported={(fileId) => props.DeleteImportedConsumptionsOfFile(providerId, fileId)}
        onDeletedImported={() => reloadAll()}
        onUndoImport={(file) => setUndoTarget({ fileId: String(file.id), fileName: fileDisplayName(file) })}
        onLinkFile={(path) => props.LinkServicesPerformedFile(providerId, path)}
      />

      <AssignProtocolPatientDrawer
        open={assignOpen || !!assignTargets}
        onDismiss={closeAssign}
        consumptions={assignTargets ?? assignItems}
        excludedCount={assignTargets ? 0 : selected.length - toAssign.length}
        LoadProtocols={props.LoadProtocolsForAssignment}
        LoadPatients={props.LoadProtocolPatients}
        Assign={props.AssignProtocolAndPatient}
        onAssigned={onAssignedFromDrawer}
      />

      <ProviderConsumptionDetailsDrawer
        item={detailItem}
        open={!!detailItem && !assignTargets}
        providerName={providerName}
        fileNameById={fileNameById}
        refreshKey={detailRefresh}
        LoadConsumption={props.LoadProviderConsumption}
        MarkToSettle={props.MarkProviderConsumptionsToSettle}
        onChanged={reloadAll}
        onAssign={(item) => setAssignTargets([item])}
        onOpenSettlement={onOpenSettlement}
        onOpenProtocolConsumption={openProtocolConsumptionFromDetail}
        onDismiss={() => setDetailItem(undefined)}
      />

      <ProviderSyncRequestDrawer
        open={syncOpen}
        onDismiss={() => setSyncOpen(false)}
        providerName={providerName}
        protocols={protocols}
        protocolsLoading={protocolsLoading}
        protocolsError={protocolsError}
        onSubmit={requestSync}
      />

      <ProviderSyncRunsDrawer
        open={runsOpen}
        onDismiss={() => setRunsOpen(false)}
        providerName={providerName}
        LoadRuns={() => props.LoadProviderSyncRuns(providerId)}
        onOpenAlarms={(run) => {
          setRunsOpen(false);
          setAlarmsRun(run);
        }}
      />

      <ProviderSyncAlarmsDrawer
        open={!!alarmsRun}
        onDismiss={() => setAlarmsRun(undefined)}
        run={alarmsRun}
        protocols={protocols}
        LoadAlarms={props.LoadProviderSyncAlarms}
        onOpenConsumption={(protocolId, consumptionId) => {
          setAlarmsRun(undefined);
          setDetail({ protocolId, consumptionId });
        }}
      />

      {canConfigureImport && (
        <ImportSettingsDrawer
          open={settingsOpen}
          onDismiss={() => setSettingsOpen(false)}
          providerName={providerName}
          templates={
            canEditTemplates
              ? {
                  LoadTemplates: () => props.LoadReferenceTemplates!(providerId),
                  CreateTemplate: (body) => props.CreateReferenceTemplate!(providerId, body),
                  UpdateTemplate: (templateId, body) => props.UpdateReferenceTemplate!(providerId, templateId, body),
                  DeleteTemplate: (templateId) => props.DeleteReferenceTemplate!(providerId, templateId),
                  ReorderTemplates: (templateIds) => props.ReorderReferenceTemplates!(providerId, templateIds),
                  TestTemplate: (parts, texts) => props.TestReferenceTemplate!(providerId, { parts, texts }),
                }
              : undefined
          }
          LoadServiceMappings={canMapServices ? (filter) => props.LoadServiceMappings!(providerId, filter) : undefined}
          UpdateServiceMapping={canMapServices ? (mappingId, medicalServiceId) => props.UpdateServiceMapping!(providerId, mappingId, medicalServiceId) : undefined}
          DeleteServiceMapping={canMapServices ? (mappingId) => props.DeleteServiceMapping!(providerId, mappingId) : undefined}
        />
      )}

      <ImportJobsHistoryDrawer
        open={jobsOpen}
        onDismiss={() => setJobsOpen(false)}
        providerName={providerName}
        LoadJobs={() => props.LoadProviderImportJobs(providerId)}
        onViewConsumptions={(job) => {
          setJobsOpen(false);
          showConsumptionsOfFile(String(job.providerFileId));
        }}
        onRetry={(job) => {
          setJobsOpen(false);
          openImport(String(job.providerFileId));
        }}
        onUndoImport={(job) => setUndoTarget(undoTargetOfJob(job))}
      />

      <UndoImportDialog
        target={undoTarget}
        onUndo={(fileId) => props.DeleteImportedConsumptionsOfFile(providerId, fileId)}
        onDismiss={() => setUndoTarget(undefined)}
        onUndone={onUndoneImport}
      />

      <Dialog open={toSettleConfirm !== undefined} modalType="alert" onOpenChange={(_event, data) => !data.open && setToSettleConfirm(undefined)}>
        <DialogSurface data-testid="pcs-to-settle-dialog">
          <DialogBody>
            <DialogTitle>{tr(toSettleConfirm ? 'toSettle.confirm.markTitle' : 'toSettle.confirm.unmarkTitle')}</DialogTitle>
            <DialogContent>
              <p>{tr(toSettleConfirm ? 'toSettle.confirm.markText' : 'toSettle.confirm.unmarkText', { count: total })}</p>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setToSettleConfirm(undefined)} data-testid="pcs-to-settle-cancel">
                {tr('toSettle.confirm.cancel')}
              </Button>
              <Button
                appearance="primary"
                onClick={() => runToSettle({ providerId, filter: toSettleFilterOf(filter), toSettle: !!toSettleConfirm })}
                data-testid="pcs-to-settle-confirm"
              >
                {tr('toSettle.confirm.accept')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={confirmRevalue} modalType="alert" onOpenChange={(_event, data) => !data.open && setConfirmRevalue(false)}>
        <DialogSurface data-testid="pcs-revalue-dialog">
          <DialogBody>
            <DialogTitle>{tri('revalue.confirmTitle')}</DialogTitle>
            <DialogContent>
              <p>{tri('revalue.confirmText')}</p>
              {filter.providerFileId && (
                <Checkbox
                  checked={revalueScopeFile}
                  onChange={(_event, data) => setRevalueScopeFile(!!data.checked)}
                  label={tri('revalue.scopeFile', { name: fileNameById.get(String(filter.providerFileId)) ?? filter.providerFileId })}
                  data-testid="pcs-revalue-scope"
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmRevalue(false)}>
                {tri('generate.cancel')}
              </Button>
              <Button appearance="primary" onClick={runRevalue} data-testid="pcs-revalue-confirm">
                {tri('revalue.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      <Dialog open={confirmGenerate}modalType="alert" onOpenChange={(_event, data) => !data.open && setConfirmGenerate(false)}>
        <DialogSurface data-testid="pcs-generate-dialog">
          <DialogBody>
            <DialogTitle>{tri('generate.confirmTitle')}</DialogTitle>
            <DialogContent>
              <p>{tri('generate.confirmText', { count: toGenerate.length })}</p>
              {selected.length > toGenerate.length && <p className="pcs-secondary">{tri('generate.excluded', { count: selected.length - toGenerate.length })}</p>}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setConfirmGenerate(false)}>
                {tri('generate.cancel')}
              </Button>
              <Button appearance="primary" onClick={runGenerate} data-testid="pcs-generate-confirm">
                {tri('generate.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {detail && (
        <ConsumptionDetailsCompoment
          ProtocolId={detail.protocolId}
          ProtocolConsumptionId={detail.consumptionId}
          ParentProtocolConsumptionId={undefined}
          visitId={undefined}
          isOpen={!!detail}
          onClose={closeDetail}
        />
      )}
    </section>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProviderConsumptionsSection);
